import { isSet } from '../utils/isSet';
import { Dropdown } from "./Dropdown";

class SiteSearch {
    constructor(root){
        this.root = root;
        this.searchForm = root.querySelector('.search-form');
        this.searchInput = root.querySelector('input[type="search"]');
        this.container = this.root.querySelector('.search-form__results');
        this.resultsContainer = this.container.querySelector('.results__inner');
        this.hireSearchResultTemplate = document.querySelector('#search-result--hire--template');
        this.trainingSearchResultTemplate = document.querySelector('#search-result--training--template');
        this.miscSearchResultTemplate = document.querySelector('#search-result--misc--template');

        this.messageTemplate = document.querySelector('#mph-message-template');

        this.timeout = 0;
        this.previousSearchString = '';
        this.jsonURL = '';

        this.hireResults = [];
        this.trainingResults = [];
        this.miscResults = [];

        this.searchInput.addEventListener(
            'keyup',
            this.onInputKeyup.bind(this)
        );

        this.searchInput.addEventListener(
            'click',
            this.onInputClick.bind(this)
        );

        this.searchInput.addEventListener(
            'focusin',
            this.onInputFocusIn.bind(this)
        );


        window.addEventListener(
            'load',
            this.init.bind(this),
            true
        );
    }

    /**
     * Checks if the passed string is different when compared with the previously stored search string this.previousSearchString
     * @param  {String}  value  The search query string that we want to check that has changed
     */
    hasChanged(value){
        if(value == this.previousSearchString){
            return false;
        }

        return true;
    }

    /**
     * Does some light string escaping
     * @param  {String}  string  The string that is to be escaped
     */
    escape(string) {
        return string
            .replace(/&/g, "&amp;")
            .replace(/</g, "&lt;")
            .replace(/>/g, "&gt;")
            .replace(/"/g, "&quot;")
            .replace(/'/g, "&#39;");
    }
    

    /**
     * Initialise things here
     */
    init(){
        this.jsonURL = this.root.getAttribute('data-fetch-url');

        this.dropdownAPI = new Dropdown(
            this.searchForm,
            {
                openOnTriggerClick: false,
                openOnArrowKeydown: false,
                setFocusOnOpen: false
            }
        )

        this.searchForm.addEventListener(
            'dropdownbeforeopen',
            this.onDropdownOpen.bind(this)
        )
    }


    /**
     * Called before the search result dropdown is opened
     * @param {*} e 
     * @returns 
     */
    onDropdownOpen(e){
        const searchValue = this.escape(this.searchInput.value);

        if(searchValue == ''){
            return false;
        }

        return true;
    }

    /**
     * Fetches the results from JSON query
     */
    async fetchJSON(){
        const cleanValue = this.escape(this.searchInput.value);
        const searchURL = this.jsonURL + cleanValue;

        let responseJSON;

        try {
            const response = await fetch(searchURL);

            if (!response.ok) {
                // Handle response error
                throw new Error(`${response.status} ${response.statusText}`);
            }

            responseJSON = await response.json();
            
        } catch (error) {
            if (error instanceof SyntaxError) {
                console.warn('There was a SyntaxError when fetching the JSON', error);
            } else {
                console.warn('There was an error', error);
            }
        }

        if (responseJSON) {
            this.getTotalCounts(responseJSON);
            this.organiseJSON(responseJSON);
        }
    }

    /**
     * Counts the total items for Hire, Training & Misc result types
     * @param {*} json JSON list of search results
     */
    getTotalCounts(json){
        const list = json.list;

        this.totalCount = list.length;
        this.hireCount = 0;
        this.trainingCount = 0;
        this.miscCount = 0;

        for (let i = 0; i < list.length; i++) {
            let result = list[i];

            switch(result.type){
                case 'hire':
                    this.hireCount++
                    break;
                case 'training':
                    this.trainingCount++
                    break;
                default:
                    this.miscCount++
            }
        }
    }

    /**
     * Gets and stores only the top JSON results for each category
     * @param {*} json JSON list of search results
     */
    organiseJSON(json){
        const list = json.list;

        this.hireResults = [];
        this.trainingResults = [];
        this.miscResults = [];

        if(
            this.hireResults.length === 5 &&
            this.trainingResults.length === 3 &&
            this.miscResults.length === 3
        ){
            this.buildResults();
            return;
        }

        for (let i = 0; i < list.length; i++) {
            let result = list[i];

            switch(result.type){
                case 'hire':
                    if(this.hireResults.length === 5) break;
                    this.hireResults.push(result);
                    break;
                case 'training':
                    if(this.trainingResults.length === 3) break;
                    this.trainingResults.push(result);
                    break;
                default:
                    if(this.miscResults.length === 3) break;
                    this.miscResults.push(result);
            }
        }

        this.buildResults();
    }

    /**
     * Handles all keyup keyboard events
     * @param  {Object}  e  The event that triggered this method
     */
    onInputKeyup(e) {
        if(!this.hasChanged(this.searchInput.value)){
            return;
        }

        this.previousSearchString = this.searchInput.value;

        if(this.searchInput.value === ''){
            this.dropdownAPI.closeDropdown();
        } else{
            if(this.dropdownAPI.isOpen === false) this.dropdownAPI.openDropdown();
        }

        if(this.searchInput.textLength <= 3){
            this.clearMessages();
            this.removeResultsHeader();
            this.resultsContainer.innerHTML = '';

            this.container.append(
                this.buildMessage('<strong>Please Keep Typing...</strong>')
            )
            return;
        }

        clearTimeout(this.timeout);

        this.clearMessages();

        if(this.resultsContainer.innerHTML != ''){
            this.container.classList.add('is-searching');
        } else{
            this.container.append(
                this.buildMessage('<strong>Searching...</strong>')
            );
        }


        this.timeout = setTimeout(
            this.fetchJSON.bind(this),
            700
        );
    }


    /**
     * Handles all click events on the search input
     * @param {*} e The event that triggered this method
     */
    onInputClick(e){
        if(this.searchInput.value === '') return;

        this.dropdownAPI.openDropdown();
    }

    /**
     * Handles all focusin events on the search input
     * @param {*} e The event that triggered this method
     */
    onInputFocusIn(e){
        if(this.searchInput.value === '') return;

        this.dropdownAPI.openDropdown();
    }

    /**
     * Begins the process of building the resulting HTML
     */
    buildResults(){
        this.clearMessages();
        this.container.classList.remove('is-searching');

        if(this.hireResults.length >= 1){
            this.buildHireResultContainer();
            this.buildHireResults();            
        } else{
            this.removeHireResultContainer();
        }

        if(this.trainingResults.length >= 1){
            this.buildTrainingResultContainer();
            this.buildTrainingResults();            
        } else{
            this.removeTrainingResultContainer();
        }

        if(this.miscResults.length >= 1){
            this.buildMiscResultContainer();
            this.buildMiscResults();            
        } else{
            this.removeMiscResultContainer();
        }

        if(
            this.hireResults.length >= 1 ||
            this.trainingResults.length >= 1 ||
            this.miscResults.length >= 1
        ){
            this.buildResultsHeader();
        } else{
            this.removeResultsHeader();
        }
    }


    /**
     * Populates and appends a hire result template
     */
    buildHireResults(){
        
        const hireResultsContainer = this.container.querySelector('.search-results__hire-container');

        for (let i = 0; i < this.hireResults.length; i++) {
            let hireResult = this.hireResults[i];

            let hireSearchResultElement = this.hireSearchResultTemplate.content.cloneNode(true).children[0];

            if(
                !isSet(hireResult.title) ||
                !isSet(hireResult.price) ||
                !isSet(hireResult.href)
            ){
                return;
            }

            hireSearchResultElement.querySelector('.search-result__title').textContent = hireResult.title;
            hireSearchResultElement.querySelector('.search-result__price').textContent = hireResult.price;
            hireSearchResultElement.querySelector('.search-result__link').href = hireResult.href;

            if(isSet(hireResult.img)){
                hireSearchResultElement.querySelector('.search-result__img').src = hireResult.img;
            }

            if(isSet(hireResultsContainer)) hireResultsContainer.appendChild(hireSearchResultElement);
        }
    }

    /**
     * Populates and appends a hire container/title
     */
    buildHireResultContainer(){
        this.removeHireResultContainer();

        const fragment = new DocumentFragment();
        const containerElm = document.createElement('div');
        containerElm.classList.add('search-results__hire-container');

        const containerTitle = document.createElement('div');
        containerTitle.classList.add('search-results__title', 'search-results__hire-title');

        const containerTitleHeading = document.createElement('h6');
        containerTitleHeading.textContent = 'Hire';

        const containerTitleLink = document.createElement('a');
        containerTitleLink.textContent = 'View All (' + this.hireCount + ')';
        containerTitleLink.href = '/search/?q=' + this.searchInput.value + '#hire'

        containerTitle.append(containerTitleHeading);
        containerTitle.append(containerTitleLink);

        fragment.append(containerTitle);
        fragment.append(containerElm);

        this.resultsContainer.appendChild(fragment);
    }

    /**
     * Removes the Hire container and the subsequent results
     */
    removeHireResultContainer(){
        const hireContainer = this.container.querySelector('.search-results__hire-container');
        const hireTitle = this.container.querySelector('.search-results__hire-title');

        if(isSet(hireContainer)) hireContainer.remove();
        if(isSet(hireTitle)) hireTitle.remove();
    }


    buildTrainingResults(){
        const trainingResultsContainer = this.container.querySelector('.search-results__training-container');

        for (let i = 0; i < this.trainingResults.length; i++) {
            let trainingResult = this.trainingResults[i];

            let trainingSearchResultElement = this.trainingSearchResultTemplate.content.cloneNode(true).children[0];

            if(
                !isSet(trainingResult.title) ||
                !isSet(trainingResult.href) ||
                !isSet(trainingResult.duration)
            ){
                return;
            }

            trainingSearchResultElement.querySelector('.search-result__title').textContent = trainingResult.title;
            trainingSearchResultElement.querySelector('.search-result__link').href = trainingResult.href;
            trainingSearchResultElement.querySelector('.search-result__duration').textContent = trainingResult.duration;

            if(isSet(trainingResult.img)){
                trainingSearchResultElement.querySelector('.search-result__img').src = trainingResult.img;
            }

            if(isSet(trainingResultsContainer)) trainingResultsContainer.appendChild(trainingSearchResultElement);
        }
    }

    buildTrainingResultContainer(){
        this.removeTrainingResultContainer();

        const fragment = new DocumentFragment();
        const containerElm = document.createElement('div');
        containerElm.classList.add('search-results__training-container');

        const containerTitle = document.createElement('div');
        containerTitle.classList.add('search-results__title', 'search-results__training-title');

        const containerTitleHeading = document.createElement('h6');
        containerTitleHeading.textContent = 'Training';

        const containerTitleLink = document.createElement('a');
        containerTitleLink.textContent = 'View All (' + this.trainingCount + ')';
        containerTitleLink.href = '/search/?q=' + this.searchInput.value + '#training'

        containerTitle.append(containerTitleHeading);
        containerTitle.append(containerTitleLink);

        fragment.append(containerTitle);
        fragment.append(containerElm);
        
        this.resultsContainer.appendChild(fragment);
    }

    removeTrainingResultContainer(){
        const trainingContainer = this.container.querySelector('.search-results__training-container');
        const trainingTitle = this.container.querySelector('.search-results__training-title');

        if(isSet(trainingContainer)) trainingContainer.remove();
        if(isSet(trainingTitle)) trainingTitle.remove();
    }

    buildMiscResults(){
        const miscResultsContainer = this.container.querySelector('.search-results__misc-container');

        for (let i = 0; i < this.miscResults.length; i++) {
            let miscResult = this.miscResults[i];

            let miscSearchResultElement = this.miscSearchResultTemplate.content.cloneNode(true).children[0];

            if(
                !isSet(miscResult.title) ||
                !isSet(miscResult.href)
            ){
                return;
            }

            miscSearchResultElement.querySelector('.search-result__title').textContent = miscResult.title;
            miscSearchResultElement.querySelector('.search-result__link').href = miscResult.href;

            if(isSet(miscResultsContainer)) miscResultsContainer.appendChild(miscSearchResultElement);
        }
    }

    buildMiscResultContainer(){
        this.removeMiscResultContainer();

        const fragment = new DocumentFragment();
        const containerElm = document.createElement('div');
        containerElm.classList.add('search-results__misc-container');

        const containerTitle = document.createElement('div');
        containerTitle.classList.add('search-results__title', 'search-results__misc-title');

        const containerTitleHeading = document.createElement('h6');
        containerTitleHeading.textContent = 'Other';

        const containerTitleLink = document.createElement('a');
        containerTitleLink.textContent = 'View All (' + this.miscCount + ')';
        containerTitleLink.href = '/search/?q=' + this.searchInput.value + '#misc'

        containerTitle.append(containerTitleHeading);
        containerTitle.append(containerTitleLink);

        fragment.append(containerTitle);
        fragment.append(containerElm);
        
        this.resultsContainer.appendChild(fragment);
    }

    removeMiscResultContainer(){
        const miscContainer = this.container.querySelector('.search-results__misc-container');
        const miscTitle = this.container.querySelector('.search-results__misc-title');

        if(isSet(miscContainer)) miscContainer.remove();
        if(isSet(miscTitle)) miscTitle.remove();
    }

    buildMessage(message, icon, classes){
        let messageElement = this.messageTemplate.content.cloneNode(true).children[0];

        if(isSet(message)) messageElement.querySelector('.mph-message__body__p').innerHTML = message;
        
        if(isSet(icon)) messageElement.querySelector('.mph-message__icon').classList.add(icon);

        if(isSet(classes)) messageElement.classList.add(classes);

        return messageElement;
    }

    clearMessages(){
        const messages = this.container.querySelectorAll('.mph-message');

        messages.forEach((message) => {
            message.remove();
        })
    }


    buildResultsHeader(){
        this.removeResultsHeader();

        const fragment = new DocumentFragment();

        const headerContainer = document.createElement('div');
        headerContainer.classList.add('results__header');
        const headerContainerTitle = document.createElement('h5');
        headerContainerTitle.textContent = 'Top Results';

        const headerContainerButton = document.createElement('a');
        headerContainerButton.href = '/search/?q=' + this.searchInput.value;
        headerContainerButton.classList.add('button', 'button--primary');

        const headerContainerButtonSpan = document.createElement('span');
        headerContainerButtonSpan.textContent = 'View All Results (' + this.totalCount + ')';

        headerContainerButton.append(headerContainerButtonSpan);

        headerContainer.append(headerContainerTitle);
        headerContainer.append(headerContainerButton);
        fragment.append(headerContainer);

        this.container.prepend(fragment);
    }

    removeResultsHeader(){
        const headerContainer = this.container.querySelector('.results__header');

        if(isSet(headerContainer)) headerContainer.remove();
    }
}

exports.SiteSearch = SiteSearch